import { useCallback } from 'react';
import {
  useGetUserContextQuery,
  userContextRtkApi,
} from '@/shared/features/user-context/infra/adapters/userContextApiSlice';
import { useAddToVideoCallCartMutation } from '../../infra/adapters/videoCall.adapter';
import { useAppDispatch, useAppSelector } from '@/app/store/store';
import { useToast } from '@/shared/widgets/toast';
import {
  setVideoCallActiveCount,
  selectVideoCallSessionActiveCount,
} from '../store/videoCallSlice';

export function useVideoCallCart() {
  const dispatch = useAppDispatch();
  const { toast } = useToast();

  const { data: userContext } = useGetUserContextQuery(undefined, {
    refetchOnMountOrArgChange: false,
  });

  const [addToCart, { isLoading }] = useAddToVideoCallCartMutation();

  const ctx = userContext as Record<string, unknown> | undefined;
  const sessionActiveCount = useAppSelector(selectVideoCallSessionActiveCount);
  const userContextCount =
    typeof ctx?.videoCallTahItemsCount === 'number' ? ctx.videoCallTahItemsCount : 0;
  // Prefer session count (set immediately on successful add) over server-derived count
  const videoCallTahItemsCount = sessionActiveCount >= 0 ? sessionActiveCount : userContextCount;
  const maxVideoCallCount =
    typeof ctx?.maxVideoCallCount === 'number' ? ctx.maxVideoCallCount : 5;

  const addToVideoCallCart = useCallback(
    async (customizationId: number) => {
      try {
        const result = await addToCart(customizationId).unwrap();
        const isError = result.status === '400';
        const message = isError ? result.message || 'Unable to add to video call cart' : result.message || 'Added to video call cart';

        toast({
          title: isError ? 'Unable to add' : 'Added to Video Call Cart',
          description: message,
          variant: isError ? 'destructive' : 'default',
          duration: 3000,
        });

        if (!isError) {
          // Update session count immediately for reactive sticky bar display
          dispatch(setVideoCallActiveCount(result.activeVideoCallCount));
          // Also patch userContext cache so other subscribers stay consistent
          dispatch(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- userContextRtkApi is typed as any
            (userContextRtkApi as any).util.updateQueryData(
              'getUserContext',
              undefined,
              (draft: Record<string, unknown>) => {
                draft['videoCallTahItemsCount'] = result.activeVideoCallCount;
              }
            )
          );
          return { success: true, message };
        }
        return { success: false, message };
      } catch (err) {
        const errData = (err as { data?: { videoCallCartMessage?: string; message?: string } })?.data;
        const errMsg = errData?.videoCallCartMessage || errData?.message || 'Failed to add to video call cart';
        toast({
          title: 'Unable to add',
          description: errMsg,
          variant: 'destructive',
          duration: 3000,
        });
        return { success: false, message: errMsg };
      }
    },
    [addToCart, dispatch, toast]
  );

  const videoCallCartMessage = typeof ctx?.videoCallCartMessage === 'string' && ctx.videoCallCartMessage ? ctx.videoCallCartMessage : undefined;
  const message = videoCallCartMessage ?? `${videoCallTahItemsCount}/${maxVideoCallCount} design(s) in trial cart`;

  return {
    addToVideoCallCart,
    videoCallTahItemsCount,
    maxVideoCallCount,
    message,
    isLoading,
  };
}
